import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EnrichmentRunPanel } from '~/components/common/enrichment-run-panel';
import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import {
  GOOGLE_PLATFORM,
  META_PLATFORM,
} from '~/features/content-enrichment/config/scheduled-enrichment-platform';
import { useCreateEnrichmentRun } from '~/features/content-enrichment/hooks/use-create-enrichment-run';
import { useScheduledEnrichmentAttributeGroups } from '~/features/content-enrichment/hooks/use-scheduled-enrichment-attribute-groups';
import { useScheduledGoogleStore } from '~/features/content-enrichment/store/scheduled-google.store';
import { useScheduledMetaStore } from '~/features/content-enrichment/store/scheduled-meta.store';
import { missingRequiredScopeFilters } from '~/features/content-enrichment/utils/default-selection-filters';
import type { AppliedProductFilters } from '~/features/experiments/types/product-metadata.types';
import type { CatalogSource } from '~/features/product-catalog/hooks/use-catalog-connections';

interface CatalogBatchButtonProps {
  /** Active catalog channel — selects the platform config + store. */
  source: CatalogSource;
  /** Selected catalog connection id. */
  connectionId: string;
  /** Selected sync/revision (null = latest). */
  syncId: string | null;
  /** Applied catalog filters; empty object = enrich the whole catalog. */
  filters: AppliedProductFilters;
  /** Products the current scope covers — shown in the panel header and footer. */
  skuCount: number;
  /** Disables the action — e.g. when the catalog has no products to enrich. */
  disabled?: boolean;
}

/**
 * Header action that starts a content-enrichment batch for the active catalog
 * (Google or Meta), scoped to whatever filters are applied. Opens the shared
 * enrichment-run panel and kicks off the platform's scheduled-enrichment flow.
 */
export function CatalogBatchButton({
  source,
  connectionId,
  syncId,
  filters,
  skuCount,
  disabled = false,
}: CatalogBatchButtonProps) {
  const { t } = useTranslation('product-catalog');
  const [open, setOpen] = useState(false);

  // Resolve the platform descriptor + persisted store by source. `store` is only
  // read imperatively inside the mutation (via getState), never called as a hook,
  // so picking it by source is safe.
  const config = source === 'google' ? GOOGLE_PLATFORM : META_PLATFORM;
  // Platform-specific attribute source: Google selects from the config-service
  // taxonomy; Meta discloses its agent's always-on catalogue + post-step switches.
  // Always enabled (not gated on `open`) so it's warm by the time the panel
  // opens — the underlying query is `staleTime`/`gcTime: Infinity` (baked into
  // the agent image), so fetching it on mount costs one request per session.
  const attributeGroups = useScheduledEnrichmentAttributeGroups(config.platform, true);
  const store = source === 'google' ? useScheduledGoogleStore : useScheduledMetaStore;
  const createRun = useCreateEnrichmentRun({
    config,
    store,
    connectionId,
    syncId,
    filters,
    onCreated: () => {
      setOpen(false);
    },
  });

  // Names the scope the run will cover — the filtered subset when filters are on,
  // the whole catalog otherwise.
  const scopeLabel =
    Object.keys(filters).length > 0 ? t('createBatch.scope.filtered') : t('createBatch.scope.all');

  // The backend rejects a run whose scope misses a required filter (GMC: country, language,
  // channel, availability; Meta: availability), so block it here instead of a later 400.
  const missingRequired = missingRequiredScopeFilters(config.requiredFilters, filters);
  const blocked = disabled || missingRequired.length > 0;

  const button = (
    <Button
      size="sm"
      disabled={blocked}
      className="cursor-pointer disabled:cursor-not-allowed"
      onClick={() => {
        if (!blocked) setOpen(true);
      }}
    >
      <Plus aria-hidden="true" className="h-4 w-4" />
      {t('createBatch.cta')}
    </Button>
  );

  return (
    <>
      {missingRequired.length > 0 && !disabled ? (
        // Disabled buttons swallow pointer events for the tooltip, so a span carries the trigger.
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-not-allowed">{button}</span>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {t('createBatch.requiredFilterMissing', { filters: missingRequired.join(', ') })}
          </TooltipContent>
        </Tooltip>
      ) : (
        button
      )}

      <EnrichmentRunPanel
        open={open}
        onOpenChange={setOpen}
        skuCount={skuCount}
        scopeLabel={scopeLabel}
        groups={attributeGroups.groups}
        isLoading={attributeGroups.isLoading}
        isError={attributeGroups.isError}
        onRetry={attributeGroups.refetch}
        isSubmitting={createRun.isPending}
        onSubmit={createRun.mutate}
        // meta-agent ships no retailer prompts — the picker is Google-only.
        showRetailerPrompt={config.platform !== 'meta'}
        // Translation is google-agent's post-step and only GMC delivers a locale.
        showLanguages={config.platform !== 'meta'}
      />
    </>
  );
}
